import { onDocumentLoaded } from '@theme/utilities';
import {
  getScrollTop,
  getScrollEventTarget,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

/** @typedef {{ sx: number, sy: number, tx: number, ty: number, r: number, g: number, b: number, a: number, dx: number, dy: number, size: number }} Particle */

const MAX_PARTICLES = 600;
const PARTICLE_SAMPLE_STEP = 1;
const PARTICLE_START_THRESHOLD = 0.02;

/**
 * @param {number} value
 * @param {number} min
 * @param {number} max
 */
function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

/**
 * @param {number} progress
 */
function easeOutCubic(progress) {
  return 1 - Math.pow(1 - progress, 3);
}

/**
 * Samples dust particles from a logo image.
 *
 * @param {HTMLImageElement} image
 * @param {DOMRect} sourceRect
 * @param {DOMRect} targetRect
 * @returns {Particle[]}
 */
function buildParticlesFromImage(image, sourceRect, targetRect) {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return [];

  const drawWidth = Math.max(1, Math.round(sourceRect.width));
  const drawHeight = Math.max(1, Math.round(sourceRect.height));
  const scale = Math.min(1, 140 / Math.max(drawWidth, drawHeight));

  canvas.width = Math.max(1, Math.round(drawWidth * scale));
  canvas.height = Math.max(1, Math.round(drawHeight * scale));

  try {
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const imageData = context.getImageData(0, 0, canvas.width, canvas.height).data;
    const particles = [];
    const targetScale = targetRect.width / sourceRect.width;
    const dispersion = Math.max(sourceRect.width, sourceRect.height) * 0.45;

    for (let y = 0; y < canvas.height; y += PARTICLE_SAMPLE_STEP) {
      for (let x = 0; x < canvas.width; x += PARTICLE_SAMPLE_STEP) {
        if (particles.length >= MAX_PARTICLES) break;

        const index = (y * canvas.width + x) * 4;
        const alpha = imageData[index + 3];
        if (alpha < 100) continue;

        const relX = (x / canvas.width - 0.5) * drawWidth;
        const relY = (y / canvas.height - 0.5) * drawHeight;
        const angle = Math.random() * Math.PI * 2;
       const dispersion = Math.max(drawWidth, drawHeight) * 1.2;
const radius = dispersion * (0.35 + Math.random() * 0.65);

       

     particles.push({
    sx: relX,
    sy: relY,

    tx: relX * targetScale,
    ty: relY * targetScale,

    r: imageData[index],
    g: imageData[index + 1],
    b: imageData[index + 2],

    a: alpha / 255,

    dx: Math.cos(angle) * radius,
    dy: Math.sin(angle) * radius,

    size: Math.random() < 0.8
        ? Math.random() * 1.2
        : Math.random() * 3,

    delay: Math.random() * 0.3,

    flutter: Math.random() * Math.PI * 2,

    wind: Math.random() * 20,
});
      }
    }

    return particles;
  } catch {
    return [];
  }
}

/**
 * Animates the header logo from the hero section bottom to the header with a dust particle effect.
 */
class HeaderLogoScroll {
  /** @type {HTMLElement | null} */
  #header = null;

  /** @type {HTMLElement | null} */
  #logo = null;

  /** @type {HTMLElement | null} */
  #hero = null;

  /** @type {HTMLCanvasElement | null} */
  #canvas = null;

  /** @type {CanvasRenderingContext2D | null} */
  #ctx = null;

  /** @type {Particle[]} */
  #particles = [];

  /** @type {boolean} */
  #particlesReady = false;

  /** @type {number} */
  #baselineHeroBottom = 0;

  /** @type {number} */
  #travelRange = 200;

  /** @type {boolean} */
  #isSettled = false;

  /** @type {number | null} */
  #rafId = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {IntersectionObserver | null} */
  #heroObserver = null;

  /** @type {number | null} */
  #scrollIdleTimer = null;

  /** @type {boolean} */
  #scrollTracking = false;

  /** @type {number} */
  #lastViewportWidth = 0;

  /** @type {number} */
  #targetX = 0;

  /** @type {number} */
  #targetY = 0;

  /** @type {number} */
  #targetWidth = 0;

  /** @type {number} */
  #targetHeight = 0;

  /** @type {boolean} */
  #reducedMotion = false;

  /** @type {Set<EventTarget>} */
  #scrollTargets = new Set();

  constructor() {
    const isHomepage =
      document.body.classList.contains('index') ||
      document.querySelector('#MainContent')?.getAttribute('data-template') === 'index';

    if (!isHomepage) return;

    this.#header = document.querySelector('#header-component');
    this.#logo = this.#header?.querySelector('.header-logo[data-hidden-on-home-page]') ?? null;
    this.#hero = document.querySelector(
      '#MainContent .hero-wrapper:first-child .hero, #MainContent > .shopify-section:first-child .hero'
    );

    if (!(this.#header instanceof HTMLElement) || !(this.#logo instanceof HTMLElement) || !(this.#hero instanceof HTMLElement)) {
      return;
    }

    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#lastViewportWidth = window.innerWidth;
    this.#header.dataset.homeLogoScroll = 'hero';
    this.#logo.style.display = 'flex';

    if (!this.#reducedMotion) {
      this.#createCanvas();
    }

    this.#bindEvents();
    this.#scheduleInitialPosition();
  }

  #createCanvas() {
    const canvas = document.createElement('canvas');
    canvas.className = 'header-logo-particles';
    canvas.setAttribute('aria-hidden', 'true');
    canvas.style.cssText =
      'position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:calc(var(--layer-menu-drawer) + 2);opacity:0;';
    document.body.appendChild(canvas);
    this.#canvas = canvas;
    this.#ctx = canvas.getContext('2d');
    this.#resizeCanvas();
  }

  #resizeCanvas() {
    if (!this.#canvas || !this.#ctx) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.#canvas.width = Math.round(window.innerWidth * dpr);
    this.#canvas.height = Math.round(window.innerHeight * dpr);
    this.#ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  #scheduleInitialPosition() {
    const position = () => {
      this.#measure(true);
      this.#prepareParticles(true);
      this.#requestUpdate();
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(position);
    });

    window.addEventListener('load', position, { once: true });

    this.#hero.querySelectorAll('img').forEach((img) => {
      if (!(img instanceof HTMLImageElement)) return;
      if (img.complete) return;
      img.addEventListener('load', position, { once: true });
    });

    this.#logo.querySelectorAll('img').forEach((img) => {
      if (!(img instanceof HTMLImageElement)) return;
      if (img.complete) return;
      img.addEventListener('load', () => {
        this.#particlesReady = false;
        this.#prepareParticles(true);
        this.#requestUpdate();
      });
    });
  }

  #getActiveLogoImage() {
    const sectionImage = this.#logo?.querySelector('.header-logo__image-container--section img');
    if (sectionImage instanceof HTMLImageElement && sectionImage.complete && sectionImage.naturalWidth > 0) {
      return sectionImage;
    }

    const headerImage = this.#logo?.querySelector('.header-logo__image-container--header img');
    if (headerImage instanceof HTMLImageElement && headerImage.complete && headerImage.naturalWidth > 0) {
      return headerImage;
    }

    return null;
  }

  #getLogoDimensions() {
    const image = this.#getActiveLogoImage();

    if (image instanceof HTMLImageElement) {
      const rect = image.getBoundingClientRect();
      const styles = getComputedStyle(image);
      const cssHeight = parseFloat(styles.height) || image.offsetHeight;
      const cssWidth = parseFloat(styles.width) || image.offsetWidth;
      const aspect = image.naturalWidth / image.naturalHeight || 1;

      const width = rect.width || cssWidth || cssHeight * aspect || image.naturalWidth;
      const height = rect.height || cssHeight || image.naturalHeight;

      if (width > 0 && height > 0) {
        return { width, height };
      }
    }

    const logoImage = this.#logo?.querySelector('img');
    if (logoImage instanceof HTMLImageElement && logoImage.naturalWidth > 0) {
      const aspect = logoImage.naturalWidth / logoImage.naturalHeight;
      const height = logoImage.naturalHeight > 200 ? 36 : logoImage.naturalHeight;
      return { width: height * aspect, height };
    }

    return { width: this.#logo?.offsetWidth || 48, height: this.#logo?.offsetHeight || 36 };
  }

  /**
   * @param {boolean} [force]
   */
  #prepareParticles(force = false) {
    if (this.#reducedMotion) return;
    if (this.#particlesReady && !force) return;

    const image = this.#getActiveLogoImage();
    if (!(image instanceof HTMLImageElement)) return;

    const sourceSize = this.#getLogoDimensions();
    if (sourceSize.width <= 0 || sourceSize.height <= 0) return;

    const sourceRect = new DOMRect(0, 0, sourceSize.width, sourceSize.height);
    const targetRect = new DOMRect(
      0,
      0,
      this.#targetWidth > 0 ? this.#targetWidth : sourceSize.width,
      this.#targetHeight > 0 ? this.#targetHeight : sourceSize.height
    );

    const particles = buildParticlesFromImage(image, sourceRect, targetRect);
    if (particles.length > 0) {
      this.#particles = particles;
      this.#particlesReady = true;
    }
  }

  #bindScrollListeners() {
    const targets = [
      window,
      document,
      document.documentElement,
      document.scrollingElement,
      getScrollEventTarget(),
    ].filter(Boolean);

    for (const target of targets) {
      if (this.#scrollTargets.has(target)) continue;
      target.addEventListener('scroll', this.#onScroll, { passive: true });
      this.#scrollTargets.add(target);
    }
  }

  #unbindScrollListeners() {
    for (const target of this.#scrollTargets) {
      target.removeEventListener('scroll', this.#onScroll);
    }
    this.#scrollTargets.clear();
  }

  #bindEvents() {
    this.#bindScrollListeners();
    scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
    window.addEventListener('resize', this.#onResize);

    window.addEventListener('touchstart', this.#onTouchActivity, { passive: true });
    window.addEventListener('touchmove', this.#onTouchActivity, { passive: true });
    window.addEventListener('touchend', this.#onTouchActivity, { passive: true });
    window.addEventListener('wheel', this.#onScroll, { passive: true });

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.#onViewportChange);
      window.visualViewport.addEventListener('scroll', this.#onScroll);
    }

    this.#heroObserver = new IntersectionObserver(
      () => {
        this.#requestUpdate();
      },
      {
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
      }
    );
    this.#heroObserver.observe(this.#hero);

    this.#resizeObserver = new ResizeObserver(() => {
      const widthChanged = Math.abs(window.innerWidth - this.#lastViewportWidth) > 1;
      this.#lastViewportWidth = window.innerWidth;
      this.#resizeCanvas();

      if (widthChanged) {
        this.#particlesReady = false;
      }

      this.#measure(getScrollTop() <= 5);
      this.#prepareParticles(widthChanged);
      this.#requestUpdate();
    });

    this.#resizeObserver.observe(this.#hero);
    this.#resizeObserver.observe(this.#header);
  }

  #onViewportChange = () => {
    this.#resizeCanvas();
    this.#requestUpdate();
  };

  #onTouchActivity = () => {
    this.#startScrollTracking();
    this.#requestUpdate();
  };

  #startScrollTracking() {
    if (this.#scrollTracking) return;
    this.#scrollTracking = true;
    this.#runScrollTracking();
  }

  #runScrollTracking() {
    this.#requestUpdate();

    if (this.#scrollIdleTimer !== null) {
      clearTimeout(this.#scrollIdleTimer);
    }

    this.#scrollIdleTimer = window.setTimeout(() => {
      this.#scrollTracking = false;
      this.#requestUpdate();
    }, 120);

    if (this.#scrollTracking) {
      requestAnimationFrame(() => this.#runScrollTracking());
    }
  }

  #handleBreakpointChange = () => {
    this.#unbindScrollListeners();
    this.#bindScrollListeners();
    this.#resizeCanvas();
    this.#particlesReady = false;
    this.#measure(getScrollTop() <= 5);
    this.#prepareParticles(true);
    this.#requestUpdate();
  };

  #onScroll = () => {
    this.#startScrollTracking();
    this.#requestUpdate();
  };

  #onResize = () => {
    this.#resizeCanvas();
    this.#particlesReady = false;
    this.#measure(getScrollTop() <= 5);
    this.#prepareParticles(true);
    this.#requestUpdate();
  };

  #requestUpdate() {
    if (this.#rafId !== null) return;

    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#update();
    });
  }

  #measure(resetBaseline = false) {
    if (!(this.#hero instanceof HTMLElement) || !(this.#header instanceof HTMLElement)) return;

    this.#measureHeaderTarget();
    const heroRect = this.#hero.getBoundingClientRect();
    const endY = this.#targetY || 36;

    if (resetBaseline || getScrollTop() <= 5) {
      this.#baselineHeroBottom = heroRect.bottom;
      const range = this.#baselineHeroBottom - endY;
      this.#travelRange = Math.max(120, Math.min(range, heroRect.height * 0.5));
    }
  }

  #measureHeaderTarget() {
    if (!(this.#logo instanceof HTMLElement) || !(this.#header instanceof HTMLElement)) return;

    this.#clearInlineStyles();
    this.#header.dataset.homeLogoScroll = 'settled';
    this.#logo.dataset.logoScrollState = 'header';
    this.#logo.style.visibility = 'hidden';
    this.#logo.style.display = 'flex';

    const rect = this.#logo.getBoundingClientRect();
    if (rect.width > 0) {
      this.#targetX = rect.left + rect.width / 2;
      this.#targetY = rect.top + rect.height / 2;
      this.#targetWidth = rect.width;
      this.#targetHeight = rect.height;
    }

    this.#logo.style.visibility = '';
    this.#isSettled = false;
  }

  #getProgress() {
    if (!(this.#hero instanceof HTMLElement)) return 0;

    if (this.#reducedMotion) {
      const traveled = this.#baselineHeroBottom - this.#hero.getBoundingClientRect().bottom;
      return traveled > 0 ? 1 : 0;
    }

    const traveled = this.#baselineHeroBottom - this.#hero.getBoundingClientRect().bottom;
    const range = this.#travelRange || 200;

    return clamp(traveled / range, 0, 1);
  }

  #getAnchors() {
    if (!(this.#hero instanceof HTMLElement)) {
      return { startX: 0, startY: 0, endX: this.#targetX, endY: this.#targetY };
    }

    const heroRect = this.#hero.getBoundingClientRect();
    return {
      startX: heroRect.left + heroRect.width / 2,
      startY: heroRect.bottom,
      endX: this.#targetX,
      endY: this.#targetY,
    };
  }

  #clearInlineStyles() {
    if (!(this.#logo instanceof HTMLElement)) return;

    this.#logo.style.position = '';
    this.#logo.style.left = '';
    this.#logo.style.top = '';
    this.#logo.style.transform = '';
    this.#logo.style.zIndex = '';
    this.#logo.style.width = '';
    this.#logo.style.willChange = '';
    this.#logo.style.display = '';
    this.#logo.style.opacity = '';
    this.#logo.style.pointerEvents = '';
    this.#logo.style.filter = '';
    this.#logo.style.visibility = '';
    this.#logo.style.removeProperty('--header-logo-section-opacity');
    this.#logo.style.removeProperty('--header-logo-header-opacity');
  }

  #syncLogoVariant(progress) {
    if (!(this.#logo instanceof HTMLElement) || !this.#logo.hasAttribute('data-dual-home-logos')) return;

    const easedProgress = easeOutCubic(progress);
    this.#logo.style.setProperty('--header-logo-section-opacity', String(1 - easedProgress));
    this.#logo.style.setProperty('--header-logo-header-opacity', String(easedProgress));
  }
  

  #hideCanvas() {
    if (this.#canvas) {
      this.#canvas.style.opacity = '0';
    }
    if (this.#ctx && this.#canvas) {
      this.#ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
    }
  }

  /**
   * @param {number} progress
   */
  #drawParticles(progress) {
    if (!this.#ctx || !this.#canvas || this.#particles.length === 0) return;

    const { startX, startY, endX, endY } = this.#getAnchors();
    const phase = clamp(
    (progress - PARTICLE_START_THRESHOLD) /
    (1 - PARTICLE_START_THRESHOLD),
    0,
    1
);
    const eased = easeOutCubic(phase);
    const dust = Math.sin(phase * Math.PI);
    const fadeIn = clamp(phase / 0.12, 0, 1);
    const fadeOut = phase > 0.88 ? clamp((1 - phase) / 0.12, 0, 1) : 1;
    const canvasOpacity = fadeIn * fadeOut;

    this.#canvas.style.opacity = String(canvasOpacity);
    this.#ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);

    for (const particle of this.#particles) {
      const localProgress = clamp(
    (phase - particle.delay) /
    (1 - particle.delay),
    0,
    1
);

const eased = localProgress;

const dust = Math.sin(localProgress * Math.PI);

const windX = 35 * eased;

const windY = -8 * eased;

const flutter =
    Math.sin(
        performance.now() * 0.004 +
        particle.flutter
    ) * 3;

const baseX =
    startX +
    particle.sx +
    (endX + particle.tx - startX - particle.sx) *
    eased;

const baseY =
    startY +
    particle.sy +
    (endY + particle.ty - startY - particle.sy) *
    eased;

const x =
    baseX +
    dust * particle.dx +
    windX +
    flutter;

const y =
    baseY +
    dust * particle.dy +
    windY;

      
      const alpha = particle.a * canvasOpacity * (0.55 + (1 - dust) * 0.45);

   this.#ctx.shadowBlur = 0;

this.#ctx.shadowColor =
`rgba(${particle.r},
${particle.g},
${particle.b},
${alpha})`;

this.#ctx.fillStyle =
`rgba(${particle.r},
${particle.g},
${particle.b},
${alpha})`;

this.#ctx.beginPath();

this.#ctx.arc(
    x,
    y,
    particle.size,
    0,
    Math.PI * 2
);

this.#ctx.fill();
    }
  }

  #settle() {
    if (!(this.#header instanceof HTMLElement) || !(this.#logo instanceof HTMLElement)) return;

    this.#isSettled = true;
    this.#hideCanvas();
    this.#clearInlineStyles();
    this.#syncLogoVariant(1);
    this.#header.dataset.homeLogoScroll = 'settled';
    this.#logo.dataset.logoScrollState = 'header';
  }

  /**
   * @param {number} progress
   */
  #showLogoAtHero(progress) {
    if (
      !(this.#header instanceof HTMLElement) ||
      !(this.#logo instanceof HTMLElement) ||
      !(this.#hero instanceof HTMLElement)
    ) {
      return;
    }

    this.#isSettled = false;
    this.#header.dataset.homeLogoScroll = 'hero';
    this.#logo.dataset.logoScrollState = 'hero';

    const { startX, startY } = this.#getAnchors();
    const dissolve = clamp(progress / PARTICLE_START_THRESHOLD, 0, 1);

    this.#logo.style.position = 'fixed';
    this.#logo.style.left = `${startX}px`;
    this.#logo.style.top = `${startY}px`;
    this.#logo.style.display = 'flex';
    this.#logo.style.visibility = 'visible';
    this.#logo.style.opacity =
String(1 - dissolve);

this.#logo.style.transform =
`translate(-50%, -50%)
scale(${1 - dissolve * 0.08})`;

this.#logo.style.filter =
`blur(${dissolve * 4}px)`;
    this.#logo.style.zIndex = 'calc(var(--layer-menu-drawer) + 1)';
    this.#logo.style.width = 'max-content';
    this.#syncLogoVariant(0);

    if (dissolve <= 0) {
      this.#hideCanvas();
    }
  }

  /**
   * @param {number} progress
   */
  #animateSimple(progress) {
    if (
      !(this.#header instanceof HTMLElement) ||
      !(this.#logo instanceof HTMLElement) ||
      !(this.#hero instanceof HTMLElement)
    ) {
      return;
    }

    this.#isSettled = false;
    this.#hideCanvas();
    this.#header.dataset.homeLogoScroll = progress <= 0 ? 'hero' : 'animating';
    this.#logo.dataset.logoScrollState = progress <= 0 ? 'hero' : 'animating';

    const { startX, startY, endX, endY } = this.#getAnchors();
    const easedProgress = easeOutCubic(progress);
    const x = startX + (endX - startX) * easedProgress;
    const y = startY + (endY - startY) * easedProgress;

    this.#logo.style.position = 'fixed';
    this.#logo.style.left = `${x}px`;
    this.#logo.style.top = `${y}px`;
    this.#logo.style.display = 'flex';
    this.#logo.style.visibility = 'visible';
    this.#logo.style.opacity = '1';
    this.#logo.style.transform = 'translate(-50%, -50%)';
    this.#logo.style.filter = '';
    this.#logo.style.zIndex = 'calc(var(--layer-menu-drawer) + 1)';
    this.#logo.style.width = 'max-content';
    this.#syncLogoVariant(progress);
  }

  /**
   * @param {number} progress
   */
  #animateParticles(progress) {
    if (!(this.#header instanceof HTMLElement) || !(this.#logo instanceof HTMLElement)) return;

    this.#prepareParticles();
    this.#isSettled = false;
    this.#header.dataset.homeLogoScroll = 'animating';
    this.#logo.dataset.logoScrollState = 'animating';

    this.#logo.style.opacity = '0';
    this.#logo.style.visibility = 'hidden';
    this.#logo.style.pointerEvents = 'none';
    this.#logo.style.position = 'fixed';
    this.#logo.style.left = '-9999px';
    this.#logo.style.display = 'flex';
    this.#logo.style.filter = '';

    this.#drawParticles(progress);
    this.#syncLogoVariant(progress);
  }

  #update() {
    if (!(this.#logo instanceof HTMLElement)) return;

    const progress = this.#getProgress();

    if (progress >= 1) {
      this.#settle();
      return;
    }

    if (this.#reducedMotion) {
      if (progress <= 0) {
        this.#showLogoAtHero(0);
        return;
      }
      this.#animateSimple(progress);
      return;
    }

    if (progress <= 0) {
      this.#showLogoAtHero(0);
      return;
    }

    if (!this.#particlesReady) {
      this.#prepareParticles(true);
    }

    if (progress < PARTICLE_START_THRESHOLD) {
      this.#showLogoAtHero(progress);
      if (this.#particlesReady) {
        this.#drawParticles(progress);
      }
      return;
    }

    if (!this.#particlesReady) {
      this.#animateSimple(progress);
      return;
    }

    this.#animateParticles(progress);
  }

  destroy() {
    this.#unbindScrollListeners();
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    window.removeEventListener('resize', this.#onResize);
    window.removeEventListener('touchstart', this.#onTouchActivity);
    window.removeEventListener('touchmove', this.#onTouchActivity);
    window.removeEventListener('touchend', this.#onTouchActivity);
    window.removeEventListener('wheel', this.#onScroll);
    window.visualViewport?.removeEventListener('resize', this.#onViewportChange);
    window.visualViewport?.removeEventListener('scroll', this.#onScroll);
    this.#heroObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    this.#canvas?.remove();
    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
    }
    if (this.#scrollIdleTimer !== null) {
      clearTimeout(this.#scrollIdleTimer);
    }
  }
}

onDocumentLoaded(() => {
  new HeaderLogoScroll();
});
